<template>
  <q-dialog v-model="visible" persistent>
    <q-card style="min-width: 640px; max-width: 960px">
      <q-card-section>
        <div class="text-h6">{{ $t('composites.PartnerDedup.confirm.title') }}</div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <p class="q-mb-md">{{ $t('composites.PartnerDedup.confirm.body') }}</p>

        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-6">
            <div class="text-subtitle2 text-positive">
              {{ $t('composites.PartnerDedup.confirm.keeper') }}
            </div>
            <div class="text-body2">{{ keeper?.name }}</div>
            <div class="text-caption text-grey-7">{{ keeper?.legal_name }}</div>
          </div>
          <div class="col-6">
            <div class="text-subtitle2 text-negative">
              {{ $t('composites.PartnerDedup.confirm.loser') }}
            </div>
            <div class="text-body2">{{ loser?.name }}</div>
            <div class="text-caption text-grey-7">{{ loser?.legal_name }}</div>
          </div>
        </div>

        <q-banner v-if="loading" class="bg-grey-2 text-grey-9" rounded>
          {{ $t('composites.PartnerDedup.confirm.loading') }}
        </q-banner>

        <template v-else>
          <q-banner v-if="!conflicts.length && !drops.length" class="bg-positive text-white q-mb-md" rounded>
            {{ $t('composites.PartnerDedup.confirm.no_conflicts') }}
          </q-banner>

          <div v-if="conflicts.length" class="q-mb-md">
            <div class="text-subtitle2 q-mb-sm text-warning">
              {{ $t('composites.PartnerDedup.confirm.conflicts_title', { count: conflicts.length }) }}
            </div>
            <q-card v-for="c in conflicts" :key="c.key" flat bordered class="q-mb-sm">
              <q-card-section>
                <div class="text-subtitle2 q-mb-xs">{{ c.label }}</div>
                <div class="row q-col-gutter-md">
                  <div class="col-6">
                    <q-radio
                      v-model="resolutions[c.key]"
                      val="keeper"
                      :label="$t('composites.PartnerDedup.confirm.use_keeper')"
                      color="positive"
                    />
                    <pre class="bg-grey-9 text-white q-pa-sm rounded-borders text-caption">{{ formatRow(c.keeper) }}</pre>
                  </div>
                  <div class="col-6">
                    <q-radio
                      v-model="resolutions[c.key]"
                      val="loser"
                      :label="$t('composites.PartnerDedup.confirm.use_loser')"
                      color="warning"
                    />
                    <pre class="bg-grey-9 text-white q-pa-sm rounded-borders text-caption">{{ formatRow(c.loser) }}</pre>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>

          <div v-if="drops.length" class="q-mb-md">
            <div class="text-caption text-grey-7">
              {{ $t('composites.PartnerDedup.confirm.drops_note', { count: drops.length }) }}
              <span class="q-mx-xs">—</span>
              {{ drops.map((d) => d.label).join(', ') }}
            </div>
          </div>
        </template>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn v-close-popup flat :label="$t('composites.PartnerDedup.confirm.cancel')" />
        <q-btn
          color="negative"
          unelevated
          :disable="loading || hasUnresolvedConflicts"
          :label="$t('composites.PartnerDedup.confirm.confirm')"
          :loading="submitting"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { MergeConflict, MergeDrop, PartnerSummary } from './usePartnerDedup'

const props = defineProps<{
  modelValue: boolean
  keeper: PartnerSummary | null
  loser: PartnerSummary | null
  loadPreview: (keeperId: string, loserId: string) => Promise<{ conflicts: MergeConflict[]; drops: MergeDrop[] }>
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', resolutions: Record<string, 'keeper' | 'loser'>): void
}>()

const submitting = ref(false)
const loading = ref(false)
const conflicts = ref<MergeConflict[]>([])
const drops = ref<MergeDrop[]>([])
const resolutions = ref<Record<string, 'keeper' | 'loser'>>({})

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const hasUnresolvedConflicts = computed(() =>
  conflicts.value.some((c) => !resolutions.value[c.key]),
)

watch(
  () => props.modelValue,
  async (open) => {
    if (!open || !props.keeper || !props.loser) return
    loading.value = true
    conflicts.value = []
    drops.value = []
    resolutions.value = {}
    try {
      const result = await props.loadPreview(props.keeper.id, props.loser.id)
      conflicts.value = result.conflicts ?? []
      drops.value = result.drops ?? []
      for (const c of conflicts.value) resolutions.value[c.key] = 'keeper'
    } finally {
      loading.value = false
    }
  },
)

function formatRow(row: Record<string, unknown>): string {
  const filtered: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) {
    if (['id', 'model_id', 'model_type', 'created_at', 'updated_at', 'business_partner_id'].includes(k)) continue
    if (v === null || v === '' || typeof v === 'undefined') continue
    filtered[k] = v
  }
  return JSON.stringify(filtered, null, 2)
}

async function onConfirm() {
  submitting.value = true
  try {
    emit('confirm', { ...resolutions.value })
  } finally {
    submitting.value = false
  }
}
</script>
